import { useEffect, useState } from "react";
import { Temporal } from "@js-temporal/polyfill";
import { Activity } from "lucide-react";
import { getTagMeasurementsForRCA } from "@/api/measurement-api.js";
import KeySensorSignalsBeforeShutdown from "@/components/root-cause-analysis/key-sensor-signals-before-shutdown.jsx";
import RcaMeasurementFilter from "@/components/root-cause-analysis/rca-measurement-filter.jsx";
import {
  loadRcaMeasurementsConfig,
  patchRcaMeasurementsConfig,
} from "@/components/root-cause-analysis/rca-measurements-config-storage.js";
import MeasurementsSectionsLoading from "@/components/root-cause-analysis/measurements-loading-sections.jsx";
import RcaMeasurementsConfigure from "@/components/root-cause-analysis/rca-measurements-configure.jsx";
import PreShutdownDriftSummary from "@/components/root-cause-analysis/pre-shutdown-drift-summary.jsx";
import RemainingSensorsCollapsible from "@/components/root-cause-analysis/remaining-sensors-collapsible.jsx";
import { Button } from "@/components/ui/button.jsx";
import {
  dateRangePillIdle,
  dateRangePillSelected,
  dateRangePillTrack,
} from "@/lib/status-styles.js";
import { cn } from "@/lib/utils.js";

/** @type {readonly number[]} */
const HOUR_RANGE_OPTIONS = [3, 6, 12, 24];
/**
 * @typedef {{
 *   filterText: string,
 *   sortMode: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode,
 *   sortDirection: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection,
 * }} MeasurementSectionUiState
 */

/**
 * Displays measurements preceding a selected shutdown event. Users can choose a date range for their measurements.
 * Measurements are retrieved for the time frame up to the shutdown event.
 *
 * @param {{
 *   assetId: string|number,
 *   reportingDate: string|null,
 *   onContextChange?: (ctx: {
 *     rows: unknown[],
 *     hoursBack: number,
 *     isLoading: boolean,
 *     hasShutdownAnchor: boolean,
 *   }) => void,
 * }} props
 * @returns {JSX.Element}
 */
export default function Measurements({
  assetId,
  reportingDate,
  onContextChange,
}) {
  const [hoursBack, setHoursBack] = useState(6);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  /** Last `fetchKey` whose request finished (success or error); enables range controls only when it matches current key. */
  const [settledKey, setSettledKey] = useState(
    /** @type {string|null} */ (null)
  );
  /** Latest successful payload from `getTagMeasurementsForRCA` for the current fetch key. */
  const [measurementData, setMeasurementData] = useState(
    /** @type {unknown[]} */ ([])
  );
  const [overallFilterText, setOverallFilterText] = useState("");
  const [sectionUiState, setSectionUiState] = useState(
    /** @type {{ keySensors: MeasurementSectionUiState, drift: MeasurementSectionUiState, remaining: MeasurementSectionUiState }} */ ({
      keySensors: { filterText: "", sortMode: "score", sortDirection: "desc" },
      drift: { filterText: "", sortMode: "score", sortDirection: "desc" },
      remaining: { filterText: "", sortMode: "score", sortDirection: "desc" },
    })
  );
  const [rcaConfig, setRcaConfig] = useState(() => loadRcaMeasurementsConfig());

  /**
   * @param {import("@/components/root-cause-analysis/rca-measurements-config-storage.js").RcaMeasurementSectionId} sectionId
   * @param {boolean} expanded
   */
  function handleSectionExpandedChange(sectionId, expanded) {
    setRcaConfig(
      patchRcaMeasurementsConfig({
        sectionExpanded: { [sectionId]: expanded },
      })
    );
  }

  /**
   * @param {"keySensors"|"drift"|"remaining"} sectionId
   * @param {Partial<MeasurementSectionUiState>} patch
   */
  function updateSectionUiState(sectionId, patch) {
    setSectionUiState((prev) => ({
      ...prev,
      [sectionId]: { ...prev[sectionId], ...patch },
    }));
  }

  /**
   * Controlled filter/sort props for a measurement section.
   *
   * @param {"keySensors"|"drift"|"remaining"} sectionId
   */
  function getSectionUiProps(sectionId) {
    const state = sectionUiState[sectionId];
    return {
      filterText: state.filterText,
      onFilterTextChange: (value) =>
        updateSectionUiState(sectionId, { filterText: value }),
      sortMode: state.sortMode,
      onSortModeChange: (mode) =>
        updateSectionUiState(sectionId, { sortMode: mode }),
      sortDirection: state.sortDirection,
      onSortDirectionChange: (direction) =>
        updateSectionUiState(sectionId, { sortDirection: direction }),
    };
  }

  const hasShutdownAnchor =
    reportingDate != null && String(reportingDate).trim() !== "";

  const endMsForKey =
    hasShutdownAnchor && reportingDate != null
      ? Date.parse(String(reportingDate))
      : NaN;
  const anchorParseOk = !Number.isNaN(endMsForKey);
  const fetchKey =
    assetId != null && assetId !== "" && hasShutdownAnchor && anchorParseOk
      ? `${String(assetId)}|${String(reportingDate)}|${hoursBack}`
      : null;

  const rangeButtonsDisabled =
    fetchKey == null || isLoading || settledKey !== fetchKey;

  useEffect(() => {
    if (fetchKey == null) {
      setIsLoading(false);
      setSettledKey(null);
      setMeasurementData([]);
      if (hasShutdownAnchor && !anchorParseOk) {
        setError("Invalid shutdown time");
      } else {
        setError(null);
      }
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      const endInstant = Temporal.Instant.fromEpochMilliseconds(endMsForKey);
      const startInstant = endInstant.subtract({ hours: hoursBack });
      const endDate = endInstant.toString({ smallestUnit: "millisecond" });
      const startDate = startInstant.toString({ smallestUnit: "millisecond" });

      try {
        const data = await getTagMeasurementsForRCA(
          String(assetId),
          startDate,
          endDate
        );
        if (!cancelled) {
          setMeasurementData(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("getTagMeasurementsForRCA failed:", err);
          setError("Failed to load measurements");
          setMeasurementData([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setSettledKey(fetchKey);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  useEffect(() => {
    onContextChange?.({
      rows: measurementData,
      hoursBack,
      isLoading,
      hasShutdownAnchor,
    });
  }, [measurementData, hoursBack, isLoading, hasShutdownAnchor, onContextChange]);

  return (
    <div className='flex w-full min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-card'>
      <div className='shrink-0 border-b border-border px-3 py-3 sm:px-4'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4'>
          <div className='flex items-start gap-2'>
            <Activity
              className='mt-0.5 h-5 w-5 shrink-0 text-brand-cyan'
              aria-hidden
            />
            <div className='min-w-0'>
              <div className='flex flex-wrap items-center gap-2'>
                <h2 className='text-base font-semibold leading-tight text-foreground'>
                  Measurements
                </h2>
                <RcaMeasurementFilter
                  filterText={overallFilterText}
                  onFilterTextChange={setOverallFilterText}
                  placeholder='Filter all measurements'
                  className='pl-2'
                />
              </div>
              <p className='mt-1 text-xs text-muted-foreground'>
                {hasShutdownAnchor
                  ? ""
                  : "Select a shutdown event to load measurements for a time range prior to the selected shutdown event."}
              </p>
            </div>
          </div>
          <div
            className={dateRangePillTrack}
            role='group'
            aria-label='Measurement time range'>
            {HOUR_RANGE_OPTIONS.map((h) => (
              <Button
                key={h}
                type='button'
                size='sm'
                variant='outline'
                disabled={rangeButtonsDisabled}
                className={cn(
                  "min-w-[3.25rem] rounded-full px-3 shadow-none font-medium",
                  hoursBack === h ? dateRangePillSelected : dateRangePillIdle,
                  hoursBack === h && "disabled:!opacity-100"
                )}
                onClick={() => setHoursBack(h)}
                aria-pressed={hoursBack === h}>
                {h}h
              </Button>
            ))}
          </div>
        </div>

        <RcaMeasurementsConfigure
          config={rcaConfig}
          onConfigChange={setRcaConfig}
        />
      </div>

      <div className='px-3 py-3 sm:px-4'>
        {!hasShutdownAnchor ? (
          <p className='text-xs text-muted-foreground'>
            Choose a row in Shutdown Events to anchor the measurement window.
          </p>
        ) : isLoading ? (
          <MeasurementsSectionsLoading
            hoursBack={hoursBack}
            sectionOrder={rcaConfig.sectionOrder}
          />
        ) : error ? (
          <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
        ) : (
          <div className='flex flex-col gap-3'>
            {rcaConfig.sectionOrder.map((sectionId) => {
              const expanded = rcaConfig.sectionExpanded[sectionId];
              const onExpandedChange = (next) =>
                handleSectionExpandedChange(sectionId, next);

              if (sectionId === "drift") {
                return (
                  <PreShutdownDriftSummary
                    key={sectionId}
                    rows={measurementData}
                    overallFilterText={overallFilterText}
                    {...getSectionUiProps("drift")}
                    hoursBack={hoursBack}
                    useAlarmLimits={rcaConfig.useAlarmLimits}
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                  />
                );
              }
              if (sectionId === "keySensors") {
                return (
                  <KeySensorSignalsBeforeShutdown
                    key={sectionId}
                    rows={measurementData}
                    overallFilterText={overallFilterText}
                    {...getSectionUiProps("keySensors")}
                    zScoreThreshold={rcaConfig.zScoreThreshold}
                    keyTrendScoreThreshold={rcaConfig.keyTrendScoreThreshold}
                    useAlarmLimits={rcaConfig.useAlarmLimits}
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                  />
                );
              }
              return (
                <RemainingSensorsCollapsible
                  key={sectionId}
                  rows={measurementData}
                  overallFilterText={overallFilterText}
                  {...getSectionUiProps("remaining")}
                  zScoreThreshold={rcaConfig.zScoreThreshold}
                  keyTrendScoreThreshold={rcaConfig.keyTrendScoreThreshold}
                  useAlarmLimits={rcaConfig.useAlarmLimits}
                  expanded={expanded}
                  onExpandedChange={onExpandedChange}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
